import { useEffect, useRef, useState } from "react";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";

import { flattenTaskTree } from "../tasks/taskViewTypes";
import type { ProjectTaskSelection } from "../tasks/ProjectTaskSurface";

export interface UseProjectRequestedTaskLaunchInput {
  /** The `task` search parameter the route validated, or null when absent. */
  readonly requestedTaskId: string | null;
  readonly tasks: readonly CmmdBoardTask[];
  readonly tasksLoading: boolean;
  /**
   * Set when the board's own fetch failed. A failed load has no reliable
   * `tasks` list, so a requested id must not be judged invalid against it.
   * Wait for a retry instead, and try again once it succeeds.
   */
  readonly tasksError: string | null;
  readonly onSelectTask: (selection: ProjectTaskSelection) => void;
  readonly openForTask: (task: CmmdBoardTask) => void | Promise<void>;
  /**
   * Clears `task` through the router's own search state (e.g. `navigate({
   * search: (prev) => ..., replace: true })`), so a later navigation that
   * carries the route's current search forward does not resurrect it. Missing
   * or explicitly `undefined` (a caller passing through its own optional
   * prop) both fall back to editing `window.history` directly, which leaves
   * the router's own search state holding the old value.
   */
  readonly clearTaskSearchParam?: (() => void) | undefined;
}

export interface UseProjectRequestedTaskLaunchResult {
  /** Set once an id was requested but no matching task exists on this board. */
  readonly invalidTaskNotice: string | null;
}

/**
 * Handles a CMMD handoff link's `?task=` parameter (`{FORGE_WEB_URL}/projects/{id}?task={taskId}`).
 * Once the board finishes loading, opens that Task's detail and starts its Work
 * Session exactly once, then strips the parameter so a refresh never replays the
 * launch. An id the board does not have shows an inline notice instead of a
 * blank page or a silent no-op.
 */
export function useProjectRequestedTaskLaunch(
  input: UseProjectRequestedTaskLaunchInput,
): UseProjectRequestedTaskLaunchResult {
  const latestInput = useRef(input);
  latestInput.current = input;
  // The last requested task id this hook has already launched (or found
  // invalid), or null when nothing is currently owed a launch. A one-time
  // boolean here previously meant a second handoff link on the same mounted
  // board (the route clears `?task=` right after a launch, so
  // `requestedTaskId` goes back to `null` before the next link sets a new
  // id) returned early forever. Tracking the id, and resetting it once the
  // param clears, lets a later link launch even if it repeats an id already
  // handled once before.
  const handledTaskIdRef = useRef<string | null>(null);
  const [invalidTaskId, setInvalidTaskId] = useState<string | null>(null);

  useEffect(() => {
    const { requestedTaskId } = input;
    if (requestedTaskId === null) {
      handledTaskIdRef.current = null;
      return;
    }
    if (handledTaskIdRef.current === requestedTaskId) return;
    if (input.tasksLoading) return;
    if (input.tasksError !== null) return;

    handledTaskIdRef.current = requestedTaskId;
    // A new request supersedes whatever notice the previous one left behind.
    setInvalidTaskId(null);
    if (latestInput.current.clearTaskSearchParam) {
      latestInput.current.clearTaskSearchParam();
    } else {
      clearRequestedTaskSearchParam();
    }

    const numericTaskId = Number(requestedTaskId);
    const task = Number.isInteger(numericTaskId)
      ? flattenTaskTree(latestInput.current.tasks).find(
          (candidate) => candidate.id === numericTaskId,
        )
      : undefined;

    if (task === undefined) {
      setInvalidTaskId(requestedTaskId);
      return;
    }

    latestInput.current.onSelectTask({ kind: "task", taskId: task.id });
    void latestInput.current.openForTask(task);
    // `input.tasks` is read only through the ref above; it stays a dependency
    // so the effect re-checks once the board finishes loading or retries.
  }, [input.requestedTaskId, input.tasksLoading, input.tasksError, input.tasks]);

  return {
    invalidTaskNotice:
      invalidTaskId !== null
        ? `Couldn't find task ${invalidTaskId}. It may have been archived or removed from this board.`
        : null,
  };
}

/**
 * Drop `?task=` without adding a history entry, so a refresh never replays
 * the launch. Only used when the caller supplies no `clearTaskSearchParam`;
 * the one production caller (the tasks route) always supplies one, which
 * also drops `tab` through the router's own search state. See that route
 * file.
 */
function clearRequestedTaskSearchParam(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("task")) return;
  url.searchParams.delete("task");
  const nextSearch = url.searchParams.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash}`,
  );
}
